/**
 * Every product threshold for the insights engine, in one place (spec D12).
 * These are product decisions, set from the data measured on 2026-09-02 —
 * tune them here, never inline.
 */

/** Below this 30-day spend an ad's CPA is noise, not signal. */
export const MIN_SPEND_30D = 1000;
/** Below this many conversions a CPA is a coin flip (1 conv = meaningless). */
export const MIN_CONVERSIONS_30D = 5;
/** An ad with no spend in this many days cannot receive budget you can act on. */
export const ACTIVE_WINDOW_DAYS = 7;
/** Fewer rankable ads than this and "ranking" is a coin toss dressed up
 *  (google today: 2). */
export const MIN_POOL_SIZE = 6;
/** Data older than this suppresses recommendations entirely: moving money on
 *  stale data is worse than no advice. */
export const STALE_AFTER_DAYS = 3;
/** Reallocation step: fraction of the donor's spend/day per round. Conservative
 *  because average CPA does not survive doubling a budget (diminishing
 *  returns — marginal CPA is worse than average CPA). */
export const REALLOC_STEP = 0.15;
/** A suggested move below this many THB/day is dust — skip the pair. */
export const MIN_SHIFT_PER_DAY = 50;
/** At most this many donor-receiver pairs per pool: legible beats exhaustive. */
export const MAX_PAIRS_PER_POOL = 3;
/** Week-over-week CPA change that counts as a mover. */
export const MOVER_THRESHOLD = 0.5;
/** Both 7-day windows need at least this spend for a WoW comparison to mean
 *  anything. */
export const MOVER_MIN_SPEND_7D = 300;
/** Display only: a worse mover at or past this change renders as a loud alert
 *  (red row); below it, as a quiet one. Presentation weight, not a filter —
 *  every mover past MOVER_THRESHOLD still appears. */
export const MOVER_SEVERE_CHANGE = 0.75;
/** Level-gap smaller than this is float/rounding dust between the two metric
 *  tables, not missing coverage — clamped to zero so no tile says "0฿". */
export const UNRANKED_MIN_SPEND = 1;

// ---------------------------------------------------------------------------
// New-ads block — judging a creative in its first days (fast-feedback design).
// ---------------------------------------------------------------------------

/** Ages the picker offers, in days. */
export const NEW_AD_AGE_OPTIONS = [1, 3, 7, 14] as const;

/**
 * An ad older than the widest new-ads window is ESTABLISHED: judged by the
 * creative-quality census, never by the new-ads block — the two partition ad
 * age so no ad is judged twice on the same screen. Derived, not restated, so
 * widening the picker can never open a gap or an overlap.
 */
export const ESTABLISHED_AFTER_DAYS = Math.max(...NEW_AD_AGE_OPTIONS);
/** Default age. Day 1 reads optimistically high; from day 2 the rate is flat. */
export const NEW_AD_AGE_DEFAULT = 3;
/** Below this many impressions nothing has been measured yet. */
export const MIN_NEW_AD_IMPRESSIONS = 200;
/** An ad needs this many impressions before it can serve as a benchmark. */
export const BENCHMARK_MIN_IMPRESSIONS = 500;
// ---------------------------------------------------------------------------
// Objective-appropriate ranking (stage 4): each pool ranks on the goal its
// campaigns were bought against. Ranking everything on CPA judged two thirds
// of the account's money by a yardstick it was never bought for.
// ---------------------------------------------------------------------------

export type RankKey = "cpa" | "cpc" | "cpm";

/**
 * awareness buys reach -> cost per 1,000 impressions; consideration buys
 * traffic/engagement -> cost per click; conversion (and retention) buy
 * outcomes -> cost per conversion. An unknown objective falls back to the
 * conservative CPA key, which keeps the old behaviour for unmapped pools.
 */
export const RANK_KEY_BY_OBJECTIVE: Record<string, RankKey> = {
  awareness: "cpm",
  consideration: "cpc",
  conversion: "cpa",
  retention: "cpa",
};

/** Volume floor for a CPC pool: below this many clicks a CPC is noise. */
export const MIN_CLICKS_30D = 100;
/** Volume floor for a CPM pool: below this many impressions a CPM is noise. */
export const MIN_IMPRESSIONS_30D = 10_000;

/**
 * Days of daily CPM evidence before a unit may read as a fixed-price
 * reservation. One or two flat days are trivially flat; three is the shape of
 * a booked rate, not an auction clearing. Measured 2026-09-17 over every
 * 30-day window since 2025-06-01: with 2 days, 24 auction ad sets would have
 * been captured at the spread below; with 3, none of 653.
 */
export const FIXED_RATE_MIN_DAYS = 3;
/**
 * Maximum relative spread (p90 − p10) / p10 of a unit's complete-day CPMs for
 * it to read as fixed-rate: 0.25%. Percentiles, not min/max, because one
 * off-rate last day made min/max miss the FB R&F02 booking in 28 of 32
 * windows, while LINE L03's ฿119.99 days (rounded 2dp) escaped the old
 * "exactly one distinct CPM" test. Nearest auction line in the same scan:
 * 0.349% at ad-set grain, about 1.4× this — the margin is thin on purpose;
 * widen only with a new measurement.
 */
export const FIXED_RATE_MAX_SPREAD = 0.0025;
/**
 * A platform tile shows its own "data through" date once the platform's newest
 * row is older than this many days. 0 days = fresh, said nowhere; 1 day = the
 * ordinary overnight lag, still not worth a line.
 */
export const PLATFORM_DATE_SHOW_AFTER_DAYS = 1;
/** Rows rendered before the list is cut — worst first, and the cut is stated. */
export const NEW_AD_MAX_ROWS = 12;
/** Plan lines rendered before the cut — biggest spend first, cut stated. */
export const PLAN_MAX_ROWS = 8;
/** Fewer established ads than this and the pool is not a benchmark. */
export const BENCHMARK_MIN_ADS = 5;

// ---------------------------------------------------------------------------
// Ad review (the per-tile "Analyze ads" panel): every ad against the ad-grain
// median of its own platform × objective. Judgement only — it moves no baht.
// ---------------------------------------------------------------------------

/** An ad whose goal metric is this many times the ad median reads as cost
 *  high; its reciprocal (rounded once, 0.67) is the cost-low line. */
export const AD_REVIEW_COST_MULTIPLIER = 1.5;
/** A CPA-pool ad with zero conversions is flagged once it has spent this many
 *  median CPAs: enough money to have bought that many conversions at the going
 *  rate, and none came. */
export const AD_REVIEW_ZERO_CONV_MULTIPLE = 2;
/** Rows rendered per group before the list is cut — biggest spend first, the
 *  cut stated as "N more". */
export const AD_REVIEW_MAX_ROWS = 8;

// ---------------------------------------------------------------------------
// Creative Performance (/creatives): the Fatigue label (creativeLabels.ts).
// ---------------------------------------------------------------------------

/** Fatigue: the second half's CTR is more than this share below the first
 *  half's. A drop of exactly this share is no label. */
export const FATIGUE_CTR_DROP = 0.25;
